// Collapsing base64 payloads for display.
//
// When the agent writes an HTML page with its images inlined, the file it
// creates contains a `data:image/png;base64,` URI running to hundreds of
// thousands of characters — on one line. That page is a good page; the
// problem is only that the payload streams into the conversation and buries
// everything around it (Matt, 11 Sep call, 00:36).
//
// So the payload is replaced, FOR DISPLAY ONLY, by a marker that says what it
// is and how big. Nothing here touches what is written to disk, what is
// copied by a Copy button, or what is sent anywhere: a caller that shows the
// collapsed form must keep the original for those.

/** Anything shorter than this is left alone — a short data URI is readable. */
export const BASE64_COLLAPSE_MIN = 256

/**
 * A `data:` URI with a base64 payload. The payload is the greedy run of
 * base64 characters after the comma; the media type in front of it is what
 * makes the marker useful, so it is captured separately.
 */
const DATA_URI = /data:([\w.+-]+\/[\w.+-]+)?((?:;[\w.+-]+=?[\w.+-]*)*);base64,([A-Za-z0-9+/=]+)/g

/** Bytes a base64 string decodes to, near enough for a label. */
function decodedBytes(payload: string): number {
  const padding = payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0
  return Math.max(0, Math.floor((payload.length * 3) / 4) - padding)
}

/** "1.4 MB", "812 KB", "97 bytes" — the size a reader can judge. */
export function humanBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Replace every long base64 data URI in `text` with a one-line marker.
 *
 * The `data:<type>;base64,` prefix is KEPT. A reader looking at generated
 * HTML needs to see that the image is inlined rather than linked — that is
 * the fact the line carries — and only the payload is noise.
 */
export function collapseBase64(text: string, minLength = BASE64_COLLAPSE_MIN): string {
  if (!text) return text
  return text.replace(DATA_URI, (whole, mediaType: string | undefined, params: string, payload: string) => {
    if (payload.length < minLength) return whole
    const kind = mediaType || 'data'
    // "…1.4 MB of image/png…", not "omitted": the same marker is used on a
    // payload still streaming in, where the size is what has arrived so far
    // and "omitted" would be a claim about a whole that does not exist yet.
    return `data:${kind}${params};base64,…${humanBytes(decodedBytes(payload))} of ${kind}…`
  })
}

/** Whether collapsing would change anything — cheap enough to ask first. */
export function hasCollapsibleBase64(text: string, minLength = BASE64_COLLAPSE_MIN): boolean {
  if (!text) return false
  DATA_URI.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = DATA_URI.exec(text)) !== null) {
    if (match[3].length >= minLength) {
      DATA_URI.lastIndex = 0
      return true
    }
  }
  return false
}
